import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { Job } from "../types/database";

// Location type for better organization (UK-based)
interface LocationDetails {
  name: string;
  address?: string;
  city?: string;
  state?: string; // Used for County in UK
  postcode?: string;
  country?: string;
  notes?: string;
}

interface JobEditFormProps {
  job: Job;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function JobEditForm({
  job,
  onSuccess,
  onCancel,
}: JobEditFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    project_number: job.project_number || "",
    title: job.title || "",
    location: job.location || "",
    prep_date: job.prep_date
      ? new Date(job.prep_date).toISOString().split("T")[0]
      : "",
    ship_date: job.ship_date
      ? new Date(job.ship_date).toISOString().split("T")[0]
      : "",
    start_date: job.start_date
      ? new Date(job.start_date).toISOString().split("T")[0]
      : "",
    end_date: job.end_date
      ? new Date(job.end_date).toISOString().split("T")[0]
      : "",
    return_date: job.return_date
      ? new Date(job.return_date).toISOString().split("T")[0]
      : "",
    status:
      job.status || ("upcoming" as "upcoming" | "in_progress" | "completed"),
    description: job.description || "",
    client: job.client || "",
  });

  // Location details state
  const [locationDetails, setLocationDetails] = useState<LocationDetails>({
    name: "",
  });

  const [error, setError] = useState("");

  // Parse location data if it's in JSON format
  const parseLocation = (locationString: string): LocationDetails => {
    try {
      return JSON.parse(locationString) as LocationDetails;
    } catch (e) {
      // If not JSON, treat as simple location name
      return { name: locationString };
    }
  };

  // Initialize location form when job data loads
  useEffect(() => {
    if (job && job.location) {
      setLocationDetails(parseLocation(job.location));
    }
  }, [job]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateJobMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");

      try {
        console.log(
          `Updating job ${job.id} for organization: ${currentOrganization.id}`
        );

        // Format location as JSON string to store structured data
        const locationString = JSON.stringify(locationDetails);

        // Convert date strings to ISO format
        const updates = {
          project_number: formData.project_number,
          title: formData.title,
          location: locationString,
          prep_date: formData.prep_date
            ? new Date(formData.prep_date).toISOString()
            : undefined,
          ship_date: formData.ship_date
            ? new Date(formData.ship_date).toISOString()
            : undefined,
          start_date: formData.start_date
            ? new Date(formData.start_date).toISOString()
            : undefined,
          end_date: formData.end_date
            ? new Date(formData.end_date).toISOString()
            : undefined,
          return_date: formData.return_date
            ? new Date(formData.return_date).toISOString()
            : undefined,
          status: formData.status,
          description: formData.description || undefined,
          client: formData.client || undefined,
          updated_by: user.id,
        };

        return await jobService.updateJob(
          job.id,
          updates,
          currentOrganization.id
        );
      } catch (error: any) {
        setError(error.message || "Failed to update job");
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", job.id] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      if (onSuccess) onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.project_number) {
      setError("Project number is required");
      return;
    }

    if (!formData.title) {
      setError("Job title is required");
      return;
    }

    if (!locationDetails.name) {
      setError("Location name is required");
      return;
    }

    updateJobMutation.mutate();
  };

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">Edit Job</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Project Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="project_number"
              value={formData.project_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Job Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Client */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client
            </label>
            <input
              type="text"
              name="client"
              value={formData.client}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="upcoming">Upcoming</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Location */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Location Details
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Location Name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.name}
                onChange={(e) =>
                  setLocationDetails({
                    ...locationDetails,
                    name: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                placeholder="Address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.address || ""}
                onChange={(e) =>
                  setLocationDetails({
                    ...locationDetails,
                    address: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  placeholder="City"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  value={locationDetails.city || ""}
                  onChange={(e) =>
                    setLocationDetails({
                      ...locationDetails,
                      city: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  County
                </label>
                <input
                  type="text"
                  placeholder="County"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  value={locationDetails.state || ""}
                  onChange={(e) =>
                    setLocationDetails({
                      ...locationDetails,
                      state: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode
                </label>
                <input
                  type="text"
                  placeholder="Postcode (e.g., SW1A 1AA)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  value={locationDetails.postcode || ""}
                  onChange={(e) =>
                    setLocationDetails({
                      ...locationDetails,
                      postcode: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  placeholder="Country"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  value={locationDetails.country || ""}
                  onChange={(e) =>
                    setLocationDetails({
                      ...locationDetails,
                      country: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Notes
              </label>
              <textarea
                placeholder="Location Notes"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.notes || ""}
                onChange={(e) =>
                  setLocationDetails({
                    ...locationDetails,
                    notes: e.target.value,
                  })
                }
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Timeline */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Timeline</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Prep Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prep Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="prep_date"
                value={formData.prep_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>

            {/* Ship Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ship Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="ship_date"
                value={formData.ship_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>

            {/* Return Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="return_date"
                value={formData.return_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateJobMutation.isPending}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateJobMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
